import React, { useEffect, useRef, useState } from 'react';
import { createChibiRig } from './chibi/chibi-rig';

// Kawaii chibi avatar that swaps expression based on the bot's live state
// AND continuously animates so it feels like a tiny video feed instead of
// a paper doll. Three layered loops + one trigger:
//
//   • breathing  — gentle scale 1 ↔ 1.025 (3.6s, infinite)
//   • blinking   — fast scaleY 1 ↔ 0.92 + opacity dip (5.4s cycle, ~120ms blink)
//   • wobble     — one-shot rotate -3 → +3 → 0 deg every 8-15s random
//   • mood swap  — cross-fade keyed on mood (250ms ease-in)
//
// Mood → asset mapping lives in `public/assets/explorer-chibi/`. See that
// folder's README for the prompt template + style spec used to generate
// the PNGs.
//
// Falls back to an emoji placeholder per-mood if the PNG is missing, so
// the rail card works fine before the art lands AND degrades gracefully
// if a single variant 404s (no broken-img icon).

const CHIBI_BASE = '/assets/explorer-chibi';

const EMOJI_FALLBACK = {
  sleeping: '😴',
  bored: '🥱',
  content: '😊',
  excited: '🤩',
  curious: '🤨',
  cheering: '🥳',
  thinking: '🤔',
  focused: '🤓',
  looking: '👀',
  searching: '🔎',
  surprised: '😲',
  proud: '😎',
  // Reserved — sprites ship + emoji fallback works now; backend triggers
  // wire later when we have the corresponding signals (loop detection,
  // game result events, first-reaction detection, etc.).
  frustrated: '😤',
  sad: '🥺',
  disappointed: '😔',
  shy: '☺️',
  // Wholesome / relatable "everyday life" moods — all parked-trigger
  // for now (sprite + emoji fallback work; backend signals come later).
  yawning: '😪',
  hungry: '🤤',
  giggling: '😆',
  dreamy: '🥰',
  mischievous: '😏',
  aha: '💡',
  dancing: '💃',
  starstruck: '🌟',
  grateful: '🙏',
  embarrassed: '😳',
};

const ALL_MOODS = [
  'sleeping', 'bored', 'content', 'excited', 'curious', 'cheering', 'thinking',
  'focused', 'looking', 'searching', 'surprised', 'proud',
  'frustrated', 'sad', 'disappointed', 'shy',
  'yawning', 'hungry', 'giggling', 'dreamy', 'mischievous',
  'aha', 'dancing', 'starstruck', 'grateful', 'embarrassed',
];

// Verb → mood routing: each activity verb maps to a distinct chibi state
// so the bot's "what am I doing" reads on the avatar's face. Splits the
// old broad "excited" bucket into:
//   • focused   — deep-work verbs (typing, running a command)
//   • looking   — passive-observation verbs (looking around, scrolling,
//                 reading the page, hovering)
//   • searching — searching specifically
//   • excited   — clicking (the only "I just acted" verb left)
const FOCUSED_VERBS = new Set(['typing', 'running a command']);
const LOOKING_VERBS = new Set(['looking around', 'scrolling', 'reading the page', 'hovering']);
const SEARCHING_VERBS = new Set(['searching']);
const CLICKING_VERBS = new Set(['clicking']);

export function deriveChibiMood({ isLive, secondsSinceLastAction, activity, cheerSignal = 0 }) {
  if (!isLive) return 'sleeping';
  if (cheerSignal >= 3) return 'cheering';
  if (secondsSinceLastAction != null && secondsSinceLastAction > 120) return 'bored';
  if (activity === 'still thinking…') return 'thinking';
  if (activity === 'navigating') return 'curious';
  if (activity && FOCUSED_VERBS.has(activity)) return 'focused';
  if (activity && LOOKING_VERBS.has(activity)) return 'looking';
  if (activity && SEARCHING_VERBS.has(activity)) return 'searching';
  if (activity && CLICKING_VERBS.has(activity)) return 'excited';
  return 'content';
}

const failedPaths = new Set();

function ChibiAvatar({
  isLive,
  secondsSinceLastAction,
  activity,
  cheerSignal = 0,
  size = 64,
  bubble = true,
  // Bypass deriveChibiMood entirely (any name from ALL_MOODS). Used by the
  // Tamagotchi shell, whose offline moods come from the pet backend rather
  // than the live activity feed. undefined → existing behavior unchanged.
  moodOverride,
  // Mount the layered animated rig (chibi-rig.js — the viewer bubble's twin)
  // instead of the PNG/emoji pipeline. Opt-in per callsite: the LIVE rail
  // bubble uses it (only 7/26 moods have PNG art, so live moods were landing
  // on the emoji fallback); the offline pet screen keeps the beloved PNGs
  // (user pick 07-11 — do not flip that callsite). Any rig failure falls
  // back to the PNG pipeline untouched.
  useRig = false,
}) {
  // Surprised — fires for ~2.5s any time the activity verb changes (the
  // bot just transitioned to a new action). Layered on top of the base
  // mood so it can override "what the bot is doing" with "the bot just
  // started doing something new" — feels reactive instead of static.
  const prevActivityRef = useRef(null);
  const [surprised, setSurprised] = useState(false);
  useEffect(() => {
    const prev = prevActivityRef.current;
    prevActivityRef.current = activity;
    // Only fire on real transitions while live — null/null doesn't count,
    // and we want bot-coming-online to NOT trigger surprised.
    if (isLive && activity && prev && activity !== prev) {
      setSurprised(true);
      const id = setTimeout(() => setSurprised(false), 2500);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [activity, isLive]);

  const baseMood = moodOverride || deriveChibiMood({ isLive, secondsSinceLastAction, activity, cheerSignal });
  const mood = surprised ? 'surprised' : baseMood;
  // .webp since 07-28: the source sprites were 1024x1024 PNGs at ~1.4MB each
  // (9.3MB for the set) shipped to every /explore visitor and drawn at ~100-250px.
  // Re-encoded to 512px WebP = 266KB for the whole set. The .png originals stay
  // in the folder as the art source; nothing requests them.
  const assetPath = `${CHIBI_BASE}/${mood}.webp`;
  const [imgFailed, setImgFailed] = useState(() => failedPaths.has(assetPath));
  const [wobbling, setWobbling] = useState(false);
  const wobbleTimer = useRef(null);

  useEffect(() => {
    setImgFailed(failedPaths.has(assetPath));
  }, [assetPath]);

  // Random micro-wobble — every 8-15s seconds, do a small one-shot rotate
  // so the chibi doesn't sit perfectly still between mood transitions.
  // Uses recursive setTimeout (not setInterval) so we can vary delay.
  useEffect(() => {
    let cancelled = false;
    function scheduleNext() {
      const delay = 8000 + Math.random() * 7000;
      wobbleTimer.current = setTimeout(() => {
        if (cancelled) return;
        setWobbling(true);
        setTimeout(() => {
          if (cancelled) return;
          setWobbling(false);
          scheduleNext();
        }, 700);
      }, delay);
    }
    scheduleNext();
    return () => {
      cancelled = true;
      if (wobbleTimer.current) clearTimeout(wobbleTimer.current);
    };
  }, []);

  const handleImgError = () => {
    failedPaths.add(assetPath);
    setImgFailed(true);
  };

  // ── animated-rig mount (useRig callsites only) ────────────────────────
  // The rig manages its own breathing/blinks/entrances, so when it's live
  // we render just the bubble shell + a mount div and skip the PNG stack
  // and its CSS loops. Hooks stay unconditional (flags checked inside).
  const rigMountRef = useRef(null);
  const rigRef = useRef(null);
  const [rigFailed, setRigFailed] = useState(false);
  useEffect(() => {
    if (!useRig || rigFailed || !rigMountRef.current) return undefined;
    try {
      rigRef.current = createChibiRig(rigMountRef.current, { mood });
    } catch (e) {
      setRigFailed(true); // PNG pipeline takes over on next render
      return undefined;
    }
    return () => {
      try { if (rigRef.current) rigRef.current.destroy(); } catch (e) { /* noop */ }
      rigRef.current = null;
    };
    // Mount once per rig lifetime — mood updates flow through setMood below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useRig, rigFailed]);
  useEffect(() => {
    if (rigRef.current) rigRef.current.setMood(mood);
  }, [mood]);

  if (useRig && !rigFailed) {
    return (
      <div
        style={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...(bubble
            ? {
                borderRadius: '50%',
                overflow: 'hidden',
                background: 'linear-gradient(135deg, #bf94ff, #5c16c5)',
                border: '3px solid #15121e',
                boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
              }
            : null),
        }}
      >
        <div ref={rigMountRef} style={{ width: '88%', height: '88%' }} aria-label={`Claude is ${mood}`} />
      </div>
    );
  }

  // Each sprite-source change triggers React remount via key={assetPath},
  // and the new img/emoji fades in via `chibiAvatarFadeIn`.
  const inner = imgFailed ? (
    <span
      key={`emoji-${mood}`}
      style={{
        fontSize: `${Math.round(size * 0.62)}px`,
        lineHeight: 1,
        animation: 'chibiAvatarBlink 5.4s ease-in-out infinite, chibiAvatarFadeIn 0.32s ease-out',
      }}
      aria-label={`Claude is ${mood}`}
    >
      {EMOJI_FALLBACK[mood] || EMOJI_FALLBACK.content}
    </span>
  ) : (
    <img
      key={assetPath}
      src={assetPath}
      alt={`Claude is ${mood}`}
      onError={handleImgError}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        animation: 'chibiAvatarBlink 5.4s ease-in-out infinite, chibiAvatarFadeIn 0.32s ease-out',
      }}
    />
  );

  return (
    <div
      style={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(bubble
          ? {
              borderRadius: '50%',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #bf94ff, #5c16c5)',
              border: '3px solid #15121e',
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            }
          : null),
      }}
    >
      {/* Breathing wrapper — continuous gentle scale loop. Wobble is a
          one-shot class applied/removed by JS, so it can compose with
          the breathing scale (CSS multiple animations + transforms on
          separate elements). */}
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'chibiAvatarBreathe 3.6s ease-in-out infinite',
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: wobbling ? 'chibiAvatarWobble 0.7s ease-in-out' : undefined,
          }}
        >
          {inner}
        </div>
      </div>
      <style>{`
        @keyframes chibiAvatarBlink {
          0%, 88%, 100% { transform: scaleY(1);    opacity: 1; }
          92%           { transform: scaleY(0.92); opacity: 0.85; }
          96%           { transform: scaleY(1);    opacity: 1; }
        }
        @keyframes chibiAvatarBreathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.025); }
        }
        @keyframes chibiAvatarWobble {
          0%   { transform: rotate(0deg); }
          25%  { transform: rotate(-3.5deg); }
          50%  { transform: rotate(0deg); }
          75%  { transform: rotate(3.5deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes chibiAvatarFadeIn {
          0%   { opacity: 0; transform: scale(0.92); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

export default ChibiAvatar;
export { ALL_MOODS };
